import { type QueryKey, useQuery } from '@tanstack/react-query';
import * as React from 'react';
import type { ItemV2 } from '@/lib/api/types/items';
import { commonQueryKeys } from '@/lib/query-keys/common';

const EXTRAS_PROVIDER_NAME = `${commonQueryKeys.itemsTable.visibleItems}.extras`;
const DEBOUNCE_MS = 50;

export type ExtraItemsData = {
  valuesById: ReadonlyMap<number, Partial<ItemV2>>;
  missingIds: ReadonlySet<number>;
};

const emptyData = (): ExtraItemsData => ({
  valuesById: new Map(),
  missingIds: new Set(),
});

export const extrasQueryKey = (instanceKey: string): QueryKey =>
  [EXTRAS_PROVIDER_NAME, instanceKey] as const;

type FetchByIds = (
  ids: readonly number[],
  signal: AbortSignal,
) => Promise<ReadonlyMap<number, Partial<ItemV2>>>;

type RegistrationEntry = { count: number };

const arraysShallowEqual = (a: readonly number[], b: readonly number[]): boolean => {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

/**
 * Ref-counted bulk fetch for ids referenced by cells (e.g. ITEM custom-field
 * values) that the paged query has not loaded. Cells register the ids they
 * need; the result is merged into the items map alongside paged data.
 *
 * Dedup is intentionally against `loadedItemsRef` (already in the paged cache)
 * rather than dataset membership: an id in the dataset whose page hasn't
 * scrolled into view will never be fetched by paging, so a referencing cell
 * would render an indefinite skeleton if extras skipped it.
 *
 * Callers typically pair this with a slim `fetchByIds` (id/itemTypeId/name
 * only) so the unavoidable double-fetch on later scroll stays cheap, plus a
 * merge layer that lets paged data win over extras when both are present.
 */
export const useExtraItems = ({
  instanceKey,
  fetchByIds,
  loadedItemsRef,
  enabled,
}: {
  instanceKey: string;
  fetchByIds: FetchByIds;
  loadedItemsRef: React.RefObject<ReadonlyMap<number, unknown>>;
  enabled: boolean;
}) => {
  const countsRef = React.useRef(new Map<number, RegistrationEntry>());
  const [debouncedIds, setDebouncedIds] = React.useState<readonly number[]>([]);
  const debounceTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = React.useRef(true);

  const flush = React.useCallback(() => {
    if (!isMountedRef.current) return;
    setDebouncedIds(Array.from(countsRef.current.keys()));
  }, []);

  const schedule = React.useCallback(() => {
    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    debounceTimeoutRef.current = setTimeout(flush, DEBOUNCE_MS);
  }, [flush]);

  const registerExtraId = React.useCallback(
    (id: number) => {
      if (loadedItemsRef.current?.has(id)) return;
      const map = countsRef.current;
      const existing = map.get(id);
      if (existing) {
        existing.count += 1;
        return;
      }
      map.set(id, { count: 1 });
      schedule();
    },
    [loadedItemsRef, schedule],
  );

  const unregisterExtraId = React.useCallback(
    (id: number) => {
      const map = countsRef.current;
      const existing = map.get(id);
      if (!existing) return;
      existing.count -= 1;
      if (existing.count <= 0) {
        map.delete(id);
        schedule();
      }
    },
    [schedule],
  );

  React.useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    };
  }, []);

  const queryKey = React.useMemo(() => extrasQueryKey(instanceKey), [instanceKey]);
  const requestedIdsRef = React.useRef<readonly number[]>(debouncedIds);
  requestedIdsRef.current = debouncedIds;

  const query = useQuery<ExtraItemsData>({
    queryKey,
    queryFn: async ({ signal, client }): Promise<ExtraItemsData> => {
      const requested = requestedIdsRef.current;
      const previous = client.getQueryData<ExtraItemsData>(queryKey) ?? emptyData();
      const valuesById = new Map(previous.valuesById);
      const missingIds = new Set(previous.missingIds);
      const idsNeeded: number[] = [];
      for (const id of requested) {
        if (valuesById.has(id) || missingIds.has(id)) continue;
        idsNeeded.push(id);
      }
      if (idsNeeded.length === 0) {
        return valuesById.size === previous.valuesById.size ? previous : { valuesById, missingIds };
      }
      const fetched = await fetchByIds(idsNeeded, signal);
      for (const [id, value] of fetched) valuesById.set(id, value);
      for (const id of idsNeeded) {
        if (!fetched.has(id)) missingIds.add(id);
      }
      return { valuesById, missingIds };
    },
    enabled: enabled && debouncedIds.length > 0,
    staleTime: Number.POSITIVE_INFINITY,
  });

  // refetch() during an in-flight fetch is coalesced by React Query, so we
  // stash the new ids and re-issue after the in-flight query settles.
  const lastRequestedRef = React.useRef<readonly number[]>(debouncedIds);
  const inFlightRequestedRef = React.useRef<readonly number[] | null>(null);
  const { isFetching, refetch } = query;
  React.useEffect(() => {
    if (!enabled) return;
    if (arraysShallowEqual(lastRequestedRef.current, debouncedIds)) return;
    lastRequestedRef.current = debouncedIds;
    if (debouncedIds.length === 0) return;
    if (isFetching) {
      inFlightRequestedRef.current = debouncedIds;
      return;
    }
    void refetch();
  }, [debouncedIds, enabled, refetch, isFetching]);

  React.useEffect(() => {
    if (!enabled || isFetching) return;
    const pending = inFlightRequestedRef.current;
    if (!pending) return;
    inFlightRequestedRef.current = null;
    if (!arraysShallowEqual(pending, lastRequestedRef.current)) return;
    void refetch();
  }, [enabled, isFetching, refetch]);

  return {
    data: query.data,
    registerExtraId,
    unregisterExtraId,
  };
};
